import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Loader2, Sparkles } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SAMPLE_BRAG_DOC = `## Performance Review Summary

### Key Accomplishments
- **Led cross-functional project** that improved team velocity by 34%, resulting in on-time delivery of Q1 roadmap
- **Designed and shipped** the new onboarding flow, reducing user drop-off by 28% in the first 30 days
- **Mentored 2 junior engineers** through their first quarter, both now contributing independently

### Impact Metrics
| Area | Before | After |
|------|--------|-------|
| Sprint velocity | 18 pts | 24 pts |
| User onboarding | 42% completion | 70% completion |
| Code review turnaround | 48 hrs | 12 hrs |

### Goals Progress
- Promotion readiness: On track
- Technical leadership: Exceeding expectations`;

const BragDocGenerator = () => {
  const navigate = useNavigate();
  const [purpose, setPurpose] = useState('');
  const [timePeriod, setTimePeriod] = useState('6months');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [editableContent, setEditableContent] = useState('');
  const [exportError, setExportError] = useState(null);
  const [isFreeTier, setIsFreeTier] = useState(false);
  const [manualDoc, setManualDoc] = useState(null);
  const [isLoadingManual, setIsLoadingManual] = useState(false);

  useEffect(() => {
    const checkFreeTier = async () => {
      try {
        const sessionToken = localStorage.getItem('session_token');
        const res = await axios.get(`${API}/wins/free-tier-status`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        setIsFreeTier(res.data.is_free_tier);
      } catch (e) {}
    };
    checkFreeTier();
  }, []);

  // Manual brag doc generator for free tier: no AI, just formats their wins cleanly
  const handleGenerateManual = async () => {
    if (!purpose) {
      toast.error('Please select a purpose first');
      return;
    }
    setIsLoadingManual(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const dates = getTimePeriodDates();
      if (!dates) { setIsLoadingManual(false); return; }

      // Fetch wins and profile in parallel
      const [winsRes, profileRes] = await Promise.all([
        axios.get(`${API}/wins`, { headers: { 'Authorization': `Bearer ${sessionToken}` } }),
        axios.get(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${sessionToken}` } })
      ]);

      const allWins = Array.isArray(winsRes.data) ? winsRes.data : (winsRes.data.wins || []);
      const user = profileRes.data;

      // Filter wins by time period
      const wins = allWins.filter(w => {
        const d = new Date(w.win_date || w.created_at);
        return d >= dates.start && d <= dates.end;
      });

      if (wins.length === 0) {
        toast.error("No wins logged in that time period. Log some wins first.");
        setIsLoadingManual(false);
        return;
      }

      // Build the document content
      const name = user.name || 'Professional';
      const jobTitle = user.job_title || '';
      const periodLabel = timePeriod === 'overall' ? 'All Time'
        : timePeriod === 'custom' ? `${customStartDate} to ${customEndDate}`
        : timePeriod === '30days' ? 'Last 30 Days'
        : timePeriod === '3months' ? 'Last 3 Months'
        : timePeriod === '6months' ? 'Last 6 Months'
        : 'Last 12 Months';

      // Group wins by category
      const grouped = {};
      wins.forEach(w => {
        const cats = (w.category && w.category.length > 0) ? w.category : ['General'];
        cats.forEach(cat => {
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(w);
        });
      });

      let content = `## ${purpose}\n`;
      content += `**${name}${jobTitle ? ', ' + jobTitle : ''}**\n`;
      content += `*Period: ${periodLabel}*\n\n`;
      content += `---\n\n`;
      content += `## Achievements (${wins.length} total)\n\n`;

      Object.entries(grouped).forEach(([cat, catWins]) => {
        content += `### ${cat}\n`;
        catWins.forEach(w => {
          const text = (w.use_enhanced && w.enhanced_text) ? w.enhanced_text : w.original_text;
          const date = new Date(w.win_date || w.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          content += `- ${text} *(${date})*\n`;
        });
        content += '\n';
      });

      content += `---\n\n`;
      content += `*Generated by BragBox (bragbox.ai)*`;

      const doc = {
        title: `${purpose}: ${name}`,
        content,
        created_at: new Date().toISOString(),
        doc_id: `manual_${Date.now()}`,
        is_manual: true
      };

      setManualDoc(doc);
      toast.success(`Document built from ${wins.length} wins`);
    } catch (err) {
      console.error('Manual doc error:', err);
      toast.error('Could not build document. Please try again.');
    } finally {
      setIsLoadingManual(false);
    }
  };

  const handleExportManualPDF = () => {
    if (!manualDoc) return;
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 20;
      const maxWidth = 210 - margin * 2;
      let yPos = margin;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      const titleLines = doc.splitTextToSize(manualDoc.title, maxWidth);
      doc.text(titleLines, margin, yPos);
      yPos += titleLines.length * 8 + 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated ${new Date(manualDoc.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, yPos);
      yPos += 10;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, 210 - margin, yPos);
      yPos += 8;

      const cleanContent = manualDoc.content.replace(/^(##|###|---|\*\*|\*)/gm, '').replace(/[\u{1F300}-\u{1F9FF}]/gu, '');
      const lines = cleanContent.split('\n');
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);

      lines.forEach(line => {
        if (yPos > 270) { doc.addPage(); yPos = margin; }
        const trimmed = line.trim();
        if (!trimmed) { yPos += 3; return; }
        if (line.startsWith('## ') || line.startsWith('### ')) {
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
        } else if (line.startsWith('- ')) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
        }
        const wrapped = doc.splitTextToSize(trimmed, maxWidth);
        doc.text(wrapped, margin, yPos);
        yPos += wrapped.length * 5 + 2;
      });

      doc.save(`${manualDoc.doc_id}_brag_document.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF export failed. Try copying the text instead.');
    }
  };

  const purposes = [
    'Quarterly Performance Review',
    'Annual Performance Review',
    'Promotion Request',
    'Salary Negotiation',
    'Job Interview Prep',
    'General Summary'
  ];

  const getTimePeriodDates = () => {
    const end = new Date();
    let start = new Date();

    if (timePeriod === 'custom') {
      if (!customStartDate || !customEndDate) {
        toast.error('Please select both start and end dates');
        return null;
      }
      return {
        start: new Date(customStartDate),
        end: new Date(customEndDate)
      };
    }

    if (timePeriod === 'overall') {
      // Use earliest possible date to get ALL wins
      start = new Date('2020-01-01');
      return { start, end };
    }

    switch (timePeriod) {
      case '30days':
        start.setDate(start.getDate() - 30);
        break;
      case '3months':
        start.setMonth(start.getMonth() - 3);
        break;
      case '6months':
        start.setMonth(start.getMonth() - 6);
        break;
      case '12months':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        start.setMonth(start.getMonth() - 6);
    }

    return { start, end };
  };

  const handleGenerate = async () => {
    if (!purpose) {
      toast.error('Please select a purpose for your brag document');
      return;
    }

    const dates = getTimePeriodDates();
    if (!dates) return;

    const { start, end } = dates;

    setIsGenerating(true);
    try {
      const sessionToken = localStorage.getItem('session_token');

      const response = await axios.post(
        `${API}/brag-docs/generate`,
        {
          purpose,
          time_period_start: start.toISOString(),
          time_period_end: end.toISOString(),
          include_summary: true,
          include_accomplishments: true,
          include_goals: true,
          is_overall: timePeriod === 'overall'
        },
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );

      setGeneratedDoc(response.data);
      setEditableContent(response.data.content);
      setExportError(null);
      toast.success('Brag document generated!');
    } catch (error) {
      console.error('Failed to generate document:', error);
      toast.error('Failed to generate document. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const docBodyIsBroken = (docObj) => {
    const body = (docObj && docObj.content ? String(docObj.content) : '').trim();
    return !body || body.startsWith('Error generating');
  };

  const handleExportPDF = () => {
    if (!generatedDoc) return;
    if (docBodyIsBroken(generatedDoc)) {
      setExportError('This document did not generate. Regenerate it before exporting.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 25;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Helper to add page if needed
      const checkAddPage = (height) => {
        if (yPosition + height > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
        }
      };

      // Title
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 42, 68);
      const titleLines = doc.splitTextToSize(generatedDoc.title, maxWidth);
      titleLines.forEach(line => {
        doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 8;
      });
      yPosition += 7;

      // Date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(128, 128, 128);
      doc.text(new Date(generatedDoc.created_at).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }), pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;

      // Content - Parse and format markdown
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      // Remove emojis from content
      const cleanContent = generatedDoc.content.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');
      const lines = cleanContent.split('\\n');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        // Skip lines that are just labels
        if (line.trim() === 'HEADER' || line.trim() === 'FOOTER' || 
            line.trim() === 'IMPACT AT A GLANCE' || line.includes('Generated by BragDoc')) {
          continue;
        }
        
        checkAddPage(10);
        
        if (line.startsWith('## ')) {
          // Main section header
          yPosition += 5;
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(31, 42, 68);
          const headerText = line.replace('## ', '').replace(/\d+\)\s*/g, '');
          doc.text(headerText, margin, yPosition);
          yPosition += 10;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        } else if (line.startsWith('### ')) {
          // Subsection header
          yPosition += 3;
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text(line.replace('### ', ''), margin, yPosition);
          yPosition += 7;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'normal');
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          // Bullet point
          const bulletText = line.replace(/^[-*]\s+/g, '');
          // Remove markdown bold markers for PDF
          const cleanText = bulletText.replace(/\*\*/g, '');
          const wrappedLines = doc.splitTextToSize(cleanText, maxWidth - 5);
          doc.text('•', margin, yPosition);
          doc.text(wrappedLines, margin + 5, yPosition);
          yPosition += wrappedLines.length * 5 + 2;
        } else if (line.startsWith('---')) {
          // Horizontal rule
          yPosition += 3;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 5;
        } else if (line.trim()) {
          // Regular paragraph - remove markdown bold markers
          const cleanLine = line.replace(/\*\*/g, '');
          const wrappedLines = doc.splitTextToSize(cleanLine, maxWidth);
          
          wrappedLines.forEach(wrappedLine => {
            checkAddPage(7);
            doc.text(wrappedLine, margin, yPosition);
            yPosition += 6;
          });
        } else {
          // Empty line
          yPosition += 4;
        }
      }

      // Footer - Only date, no "Generated by BragDoc"
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(
          new Date(generatedDoc.created_at).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      doc.save(generatedDoc.doc_id + '_brag_document.pdf');
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF. Please try DOCX export instead.');
    }
  };

  const handleExportDOCX = async () => {
    if (!generatedDoc) return;
    if (docBodyIsBroken(generatedDoc)) {
      setExportError('This document did not generate. Regenerate it before exporting.');
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/exports/docx/${generatedDoc.doc_id}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${sessionToken}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${generatedDoc.doc_id}_brag_document.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Explicit duration plus a hard dismiss: sonner pauses its timer while
      // the cursor hovers the toast, which sits over the export buttons, so
      // without the fallback the success toast can hang there indefinitely.
      const exportToastId = toast.success('Word document downloaded', { duration: 3000 });
      setTimeout(() => toast.dismiss(exportToastId), 3200);
    } catch (error) {
      console.error('Failed to export DOCX:', error);
      toast.error('Failed to export DOCX');
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Generate Brag Document
            </h1>
            <p className="text-gray-600">Create a professional document for reviews and promotions</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Document Configuration
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                  What's this for? <span className="text-red-500">*</span>
                </label>
                <Select value={purpose} onValueChange={setPurpose}>
                  <SelectTrigger data-testid="purpose-select">
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    {purposes.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                  Time Period
                </label>
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger data-testid="time-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overall">Overall (All-Time Career Summary)</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="3months">Last 3 months</SelectItem>
                    <SelectItem value="6months">Last 6 months</SelectItem>
                    <SelectItem value="12months">Last 12 months</SelectItem>
                    <SelectItem value="custom">Custom Date Range</SelectItem>
                  </SelectContent>
                </Select>
                
                {timePeriod === 'overall' && (
                  <p className="text-sm text-amber-600 mt-2">
                    Builds a full career summary from every win, goal, and insight you have logged
                  </p>
                )}
                
                {timePeriod === 'custom' && (
                  <div className="mt-4 space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--navy-50)' }}>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                        Start Date
                      </label>
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--navy-900)' }}>
                        End Date
                      </label>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        min={customStartDate}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button
                data-testid="generate-doc-btn"
                onClick={isFreeTier ? handleGenerateManual : handleGenerate}
                disabled={(isFreeTier ? isLoadingManual : isGenerating) || !purpose}
                className="w-full py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: purpose ? 'var(--navy-900)' : '#9CA3AF',
                  color: 'white',
                  cursor: purpose ? 'pointer' : 'not-allowed',
                  opacity: purpose ? 1 : 0.6
                }}
              >
                {(isFreeTier ? isLoadingManual : isGenerating) ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isFreeTier ? 'Building document...' : 'Generating...'}
                  </>
                ) : (
                  <>
                    {isFreeTier ? 'Build My Document' : 'Generate Document'}
                    {!purpose && <span className="text-xs ml-2">(Select purpose first)</span>}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Document Preview
            </h2>

            {isFreeTier && !generatedDoc ? (
              <div>
                {/* Manual generator for free tier */}
                {!manualDoc ? (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--cream-100)' }}>
                      <Download className="w-7 h-7" style={{ color: 'var(--navy-900)' }} />
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }}>
                      Build your brag document
                    </h3>
                    <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                      We'll compile all your logged wins into a clean document you can download and edit yourself.
                    </p>
                    <Button
                      onClick={handleGenerateManual}
                      disabled={isLoadingManual || !purpose}
                      className="px-6 py-3 font-semibold mb-6"
                      style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                      data-testid="manual-generate-btn"
                    >
                      {isLoadingManual ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Building document...</>
                      ) : 'Build My Document'}
                    </Button>

                    {/* Upgrade nudge: below, not blocking */}
                    <div className="mt-4 p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 max-w-sm mx-auto">
                      <div className="flex items-center gap-2 justify-center mb-1">
                        <Sparkles className="w-4 h-4" style={{ color: 'var(--navy-900)' }} />
                        <span className="text-sm font-semibold" style={{ color: 'var(--navy-900)' }}>Want the AI version?</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">AI rewrites your wins into polished performance review language, adds impact metrics, and structures everything automatically.</p>
                      <button
                        onClick={() => navigate('/pricing')}
                        className="text-xs font-semibold underline"
                        style={{ color: 'var(--navy-900)' }}
                      >
                        See upgrade options →
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Manual doc preview */
                  <div>
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold mb-1" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }}>
                        {manualDoc.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Generated {new Date(manualDoc.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>

                    <div className="prose prose-sm max-w-none mb-6 max-h-[500px] overflow-y-auto p-6 bg-white rounded-lg border border-gray-200">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}
                        components={{
                          h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-6 mb-3" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                          p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc ml-6 mb-3 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                          strong: ({node, ...props}) => <strong style={{ color: 'var(--navy-900)' }} {...props} />,
                        }}
                      >
                        {manualDoc.content}
                      </ReactMarkdown>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                      <Button
                        onClick={handleExportManualPDF}
                        className="flex items-center gap-2 px-5 py-2.5 font-semibold"
                        style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                      >
                        <Download className="w-4 h-4" /> Download PDF
                      </Button>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(manualDoc.content);
                          toast.success('Copied to clipboard');
                        }}
                        variant="outline"
                        className="px-5 py-2.5"
                        style={{ borderColor: 'var(--navy-900)', color: 'var(--navy-900)' }}
                      >
                        Copy as Text
                      </Button>
                      <Button
                        onClick={() => setManualDoc(null)}
                        variant="ghost"
                        className="px-5 py-2.5 text-gray-500"
                      >
                        Rebuild
                      </Button>
                    </div>

                    {/* Post-generation upgrade nudge */}
                    <div className="p-4 rounded-xl border border-dashed" style={{ borderColor: 'var(--navy-900)', backgroundColor: 'var(--cream-50)' }}>
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 mt-0.5 shrink-0" style={{ color: 'var(--navy-900)' }} />
                        <div>
                          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--navy-900)' }}>This is the manual version</p>
                          <p className="text-xs text-gray-500 mb-2">Upgrade and the AI will rewrite every win into polished performance language, add impact metrics, and structure the whole document for your specific goal automatically.</p>
                          <button onClick={() => navigate('/pricing')} className="text-xs font-semibold underline" style={{ color: 'var(--navy-900)' }}>
                            Upgrade for AI generation →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : !generatedDoc ? (
              <div className="text-center py-12 text-gray-500">
                <p>Configure and generate your document to see a preview</p>
              </div>
            ) : (
              <div>
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-1" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }}>
                    {generatedDoc.title}
                  </h3>
                  <p className="text-sm text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                    Generated on {new Date(generatedDoc.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>

                <div className="prose prose-sm max-w-none mb-6 max-h-[600px] overflow-y-auto p-6 bg-white rounded-lg border border-gray-200">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-6 mb-3" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2" style={{ color: 'var(--navy-900)', fontFamily: 'Manrope' }} {...props} />,
                      p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc ml-6 mb-3 space-y-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal ml-6 mb-3 space-y-1" {...props} />,
                      li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                      strong: ({node, ...props}) => <strong style={{ color: 'var(--navy-900)' }} {...props} />,
                      hr: ({node, ...props}) => <hr className="my-4 border-gray-300" {...props} />,
                    }}
                  >
                    {generatedDoc.content}
                  </ReactMarkdown>
                </div>

                {exportError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="export-error">
                    <p className="text-sm text-red-700">{exportError}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    data-testid="export-pdf-btn"
                    onClick={handleExportPDF}
                    variant="outline"
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export PDF
                  </Button>
                  <Button
                    data-testid="export-docx-btn"
                    onClick={handleExportDOCX}
                    variant="outline"
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Word
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default BragDocGenerator;